// @file ShapeIndicator for TLDraw to replace DefaultShapeIndicator
// this is to handle indicators when InteractTool is the current tool
// mostly copied from DefaultShapeIndicator
// @see https://github.com/tldraw/tldraw/blob/v2.1.4/packages/editor/src/lib/components/default-components/DefaultShapeIndicator.tsx
import '@@/bits/tldraw/ShapeIndicator.css'
import { isShapeApproved } from '@@/bits/whiteboard'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import { useQuickReactor, useValue } from '@tldraw/state'
import type { TLShape, TLShapeId } from '@tldraw/tlschema'
import classNames from 'classnames'
import React, { memo, useRef, useState } from 'react'
import type { Editor, ShapeUtil, TLGeoShape, TLShapeIndicatorProps } from 'tldraw'
import { useEditor } from 'tldraw'

// Define interfaces for better type safety
interface IndicatorStyleProps {
  shape: TLShape
  util: ShapeUtil<any>
  indicator: React.JSX.Element
  shapeW: number
  shapeH: number
  isUnapprovedIndicator: boolean
  isShapePendingApproval: boolean
}

// Helper function to determine indicator style and modifications
function getIndicatorAndStyle({
  shape,
  util,
  indicator,
  shapeW,
  shapeH,
  isUnapprovedIndicator,
  isShapePendingApproval,
}: IndicatorStyleProps): {
  indicator: React.JSX.Element
  style: React.CSSProperties
} {
  // Determine shape characteristics
  const isRectangleGeo = 'geo' in shape.props && shape.props.geo === 'rectangle'
  const isWhiteRectangleGeo = isRectangleGeo && (shape as TLGeoShape).props.color === 'white'
  const isText = shape.type === 'text'
  const isPadletAttachment = shape.type === 'padlet-attachment-shape'
  const isStar = 'geo' in shape.props && shape.props.geo === 'star'
  const isCloud = 'geo' in shape.props && shape.props.geo === 'cloud'
  const isArrow = 'geo' in shape.props && shape.props.geo === 'arrow-right'
  const isAnyRect =
    (indicator.type === 'rect' && indicator.props.width != null && indicator.props.height != null) || isRectangleGeo
  const isNote = shape.type === 'note'

  // Determine rendering conditions
  const isRenderingRectangleRegularIndicatorForUnapprovedIndicator =
    isShapePendingApproval && (isAnyRect || isStar || isCloud || isArrow)
  const isRenderingFadedRegularIndicator =
    !isUnapprovedIndicator && (isWhiteRectangleGeo || isText || isPadletAttachment)
  const isAddingPaddingToRegularIndicator =
    isRenderingRectangleRegularIndicatorForUnapprovedIndicator || isRenderingFadedRegularIndicator

  // Set padding based on shape type
  // Explanation of values:
  // - For Text, we don't want padding because when you resizes the text, the snapping line does not align to the outline
  // - For white rectangle geo, the padding is not 16px because it'll add extra padding to the shape and the shape size would look different before and after approving the shape
  // - For note, 24px looks nicer because the note edges are sharp.
  // - For cloud, the indicator would look better when there is no X padding on left and right

  const PADDING_Y = isText ? 0 : isWhiteRectangleGeo ? 8 : isNote ? 24 : 16
  const PADDING_X = isText ? 0 : isWhiteRectangleGeo ? 8 : isNote ? 24 : isCloud ? 0 : 16

  const width: number = indicator.props.width ?? shapeW
  const height: number = indicator.props.height ?? shapeH

  if (width == null || height == null) {
    return { indicator, style: {} }
  }

  // Calculate scaling values
  let scalingValues = {
    scaleX: 1,
    scaleY: 1,
    translateX: 0,
    translateY: 0,
  }
  if (isUnapprovedIndicator || isAddingPaddingToRegularIndicator) {
    scalingValues = {
      scaleX: (width + PADDING_X) / width,
      scaleY: (height + PADDING_Y) / height,
      translateX: -PADDING_X / 2 / ((width + PADDING_X) / width),
      translateY: -PADDING_Y / 2 / ((height + PADDING_Y) / height),
    }
  }

  // Set base style
  const baseStyle: React.CSSProperties = {
    transform: `scale(${scalingValues.scaleX}, ${scalingValues.scaleY}) translate(${scalingValues.translateX}px, ${scalingValues.translateY}px)`,
    strokeLinecap: 'round',
    transformOrigin: 'top left',
  }

  let modifiedIndicator = { ...indicator }

  // Modify indicator based on conditions
  if (isUnapprovedIndicator) {
    // Unapproved indicator
    const baseStrokeWidth = 2 / Math.max(scalingValues.scaleX, scalingValues.scaleY)
    const baseStrokeDasharray = 8 / Math.min(scalingValues.scaleX, scalingValues.scaleY)

    baseStyle.strokeWidth = `calc(${baseStrokeWidth}px * var(--tl-scale))`
    baseStyle.strokeDasharray = `calc(${baseStrokeDasharray}px * var(--tl-scale))`

    // Use rectangle instead of the indicator path for some shapes
    if (isStar || isCloud || isArrow) {
      modifiedIndicator = {
        ...modifiedIndicator,
        type: 'rect',
        props: {
          ...modifiedIndicator.props,
          d: undefined,
          width,
          height,
          rx: 8,
        },
      }
    }

    // Add rounded corner
    modifiedIndicator.props = { ...modifiedIndicator.props, rx: isNote ? 8 : isText ? 16 : isPadletAttachment ? 6 : 0 }

    // All unapproved shapes have a purple shading
    modifiedIndicator.props = { ...modifiedIndicator.props, fill: 'rgba(148, 102, 232, 0.1)' }
  } else {
    // Whenever we add padding to the regular indicator, we want to reduce the opacity. Normally when the indicator is right on top of the shape, on hover it looks like the shape is glowing, which is great.
    // But when we add padding, it just looks like an outline wrapping the shape, and opaque line is ugly, so we reduce the opacity so it looks like a hovering shadow.
    baseStyle.opacity = isAddingPaddingToRegularIndicator ? 0.5 : 1
    // Regular indicator when hover, which normally will be the shape outline
    // unless it's an unapproved shape, then it will be the same as the unapproved indicator
    if (isRenderingRectangleRegularIndicatorForUnapprovedIndicator) {
      modifiedIndicator = {
        ...modifiedIndicator,
        type: 'rect',
        props: {
          ...modifiedIndicator.props,
          d: undefined,
          width,
          height,
          rx: 8,
        },
      }
    }

    // These shapes have padding, and the padding needs rounding to the corners
    if (isRenderingFadedRegularIndicator) {
      modifiedIndicator.props = { ...modifiedIndicator.props, rx: 6 }
    }
  }

  return { indicator: modifiedIndicator, style: baseStyle }
}

// Component to render the indicator content
const IndicatorContent = ({
  shape,
  util,
  isUnapprovedIndicator,
  isShapePendingApproval,
}: {
  shape: TLShape
  util: ShapeUtil<any>
  isUnapprovedIndicator: boolean
  isShapePendingApproval: boolean
}): React.JSX.Element => {
  if (shape == null || shape.props == null) return util.indicator(shape)

  const shapeW = 'w' in shape.props ? shape.props.w : 0
  const shapeH = 'h' in shape.props ? shape.props.h : 0

  const indicator = util.indicator(shape)
  if (indicator == null) return <></>

  const { indicator: modifiedIndicator, style } = getIndicatorAndStyle({
    shape,
    util,
    indicator,
    shapeW,
    shapeH,
    isUnapprovedIndicator,
    isShapePendingApproval,
  })

  return <g style={style}>{modifiedIndicator}</g>
}

// Inner component for the indicator
const InnerIndicator = ({
  editor,
  id,
  isUnapprovedIndicator,
  isShapePendingApproval,
}: {
  editor: Editor
  id: TLShapeId
  isUnapprovedIndicator: boolean
  isShapePendingApproval: boolean
}): React.JSX.Element | null => {
  const shape = useValue('shape for indicator', () => editor.store.get(id), [editor, id])

  if (shape == null || shape.isLocked) return null

  return (
    <IndicatorContent
      key={shape.id}
      shape={shape}
      util={editor.getShapeUtil(shape)}
      isUnapprovedIndicator={isUnapprovedIndicator}
      isShapePendingApproval={isShapePendingApproval}
    />
  )
}

// Main ShapeIndicator component
export const ShapeIndicator = memo(function ShapeIndicator({
  shapeId,
  className,
  color,
  hidden,
  opacity,
}: TLShapeIndicatorProps) {
  const editor = useEditor()
  const [isShapePendingApproval, setIsShapePendingApproval] = useState(true)

  // Refs for SVG elements
  const rIndicator = useRef<SVGSVGElement>(null)
  const rIndicatorUnapproved = useRef<SVGSVGElement>(null)
  const rContainer = useRef<SVGGElement>(null)
  const rContainerOuterStroke = useRef<SVGGElement>(null)
  const rContainerUnapprovedStroke = useRef<SVGGElement>(null)

  // Store hooks
  const whiteboardStore = useWhiteboardStore()
  const whiteboardScriptStore = useWhiteboardScriptStore()
  const whiteboardLinkingStore = useWhiteboardLinkingStore()

  // Determine if interact outline should be shown
  const shouldShowInteractOutline = useValue(
    'show interact outline',
    () =>
      editor.isInAny('interact') &&
      editor.getHoveredShapeId() === shapeId &&
      (whiteboardScriptStore.checkIfScriptExists(shapeId) || whiteboardLinkingStore.checkIfLinkingExists(shapeId)) &&
      !whiteboardStore.isFrameBreakout, // We don't want users to be able to navigate frames in breakout mode
    [editor, shapeId],
  )
  const shouldHideHoverOutlineForShapes = useValue(
    'hide hover outline for shapes',
    () => {
      return editor.isInAny('select') && editor.getOnlySelectedShapeId() === shapeId
    },
    [editor, shapeId],
  )

  /**
   * Outline for hovered shapes for Interact tool
   */

  useQuickReactor(
    'indicator transform',
    () => {
      const elm = rIndicator.current
      if (elm == null) return
      const pageTransform = editor.getShapePageTransform(shapeId)
      if (pageTransform == null) return
      elm.style.setProperty('transform', pageTransform.toCssString())
    },
    [editor, shapeId],
  )

  useQuickReactor(
    'indicator for unapproved shapes transform',
    () => {
      const elm = rIndicatorUnapproved.current
      if (elm == null) return
      const pageTransform = editor.getShapePageTransform(shapeId)
      if (pageTransform == null) return
      elm.style.setProperty('transform', pageTransform.toCssString())
    },
    [editor, shapeId],
  )

  useQuickReactor(
    'interact outline show/hide',
    () => {
      const elm = rIndicator.current
      const containerElm = rContainer.current
      const containerOuterStrokeElm = rContainerOuterStroke.current
      if (elm == null) return
      if (containerElm == null) return
      if (containerOuterStrokeElm == null) return

      if (shouldShowInteractOutline) {
        elm.style.setProperty('display', 'block')
        containerElm.setAttribute('stroke', '#FF4081')
        containerOuterStrokeElm.setAttribute('stroke', 'rgba(255, 64, 129, 0.4)')
        containerOuterStrokeElm.style.setProperty('display', 'block')
      } else {
        elm.style.setProperty('display', hidden === true || shouldHideHoverOutlineForShapes ? 'none' : 'block')
        containerElm.setAttribute('stroke', color ?? 'var(--color-selected)')
        containerOuterStrokeElm.setAttribute('stroke', '')
        containerElm.style.setProperty('fill', 'none')
        containerOuterStrokeElm.style.setProperty('display', 'none')
      }
    },
    [hidden, color, shouldShowInteractOutline, shouldHideHoverOutlineForShapes],
  )

  /**
   * Unapproved shapes outline
   */

  // useQuickReactor(
  //   'show/hide hover indicator',
  //   () => {
  //     const elm = rIndicator.current
  //     if (elm == null) return
  //     elm.style.setProperty('display', hidden === true ? 'none' : 'block')
  //   },
  //   [isShapePendingApproval, shouldShowInteractOutline],
  // )

  useQuickReactor(
    'show/hide unapproved shape indicator',
    () => {
      const elm = rIndicatorUnapproved.current
      if (elm == null) return

      if (shouldShowInteractOutline || isShapePendingApproval) {
        elm.style.setProperty('display', 'block')
      } else {
        elm.style.setProperty('display', 'none')
      }
    },
    [isShapePendingApproval, shouldShowInteractOutline],
  )

  useQuickReactor(
    'update local shape approval state when shape.meta.approved changes',
    () => {
      const shape = editor.getShape(shapeId)
      setIsShapePendingApproval(!isShapeApproved(shape))
    },
    [editor, shapeId],
  )

  useQuickReactor(
    'unapproved shapes outline',
    () => {
      const containerUnapprovedStrokeElm = rContainerUnapprovedStroke.current
      if (containerUnapprovedStrokeElm == null) return

      if (!isShapePendingApproval) {
        containerUnapprovedStrokeElm.setAttribute('stroke', '')
      } else {
        containerUnapprovedStrokeElm.setAttribute('stroke', 'rgba(148, 102, 232, 1)')
      }

      if (editor.isInAny('select') && editor.getOnlySelectedShapeId() === shapeId) {
        containerUnapprovedStrokeElm.classList.add('unapproved-stroke--selected')
      } else {
        containerUnapprovedStrokeElm.classList.remove('unapproved-stroke--selected')
      }
    },
    [isShapePendingApproval],
  )

  return (
    <>
      <svg ref={rIndicatorUnapproved} className={classNames('tl-overlays__item', 'unapproved-indicator', className)}>
        {isShapePendingApproval && (
          <g ref={rContainerUnapprovedStroke} style={{ fill: 'none' }} opacity={opacity}>
            <InnerIndicator
              editor={editor}
              id={shapeId}
              isUnapprovedIndicator={true}
              isShapePendingApproval={isShapePendingApproval}
            />
          </g>
        )}
      </svg>
      <svg ref={rIndicator} className={classNames('tl-overlays__item', className)}>
        <g
          ref={rContainer}
          className={classNames('tl-shape-indicator')}
          stroke={color ?? 'var(--color-selected)'}
          opacity={opacity}
        >
          <InnerIndicator
            editor={editor}
            id={shapeId}
            isUnapprovedIndicator={false}
            isShapePendingApproval={isShapePendingApproval}
          />
        </g>
        <g
          ref={rContainerOuterStroke}
          style={{
            transformOrigin: 'top left',
            fill: 'none',
            contain: 'size layout',
            strokeWidth: 'calc(8px * var(--tl-scale))',
          }}
          opacity={opacity}
        >
          <InnerIndicator
            editor={editor}
            id={shapeId}
            isUnapprovedIndicator={false}
            isShapePendingApproval={isShapePendingApproval}
          />
        </g>
      </svg>
    </>
  )
})
